import React, { useState, useCallback } from 'react';
import Markdown from "@scentregroup/ui/components/markdown";
import Modal from "@scentregroup/ui/components/modal";
import { Box } from '@hub/box';
import { useToken } from '@hub/design-system-base';
import { Stack } from '@hub/stack';
import { Text } from '@hub/text';
import { Button } from '@hub/button';
import { CloudinaryImage, IMAGE_SIZES_HERO_PORTRAIT } from '../cloudinary-image';
import { useSupportingColors } from '../../hooks/use-supporting-colors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAX_MOBILE_LINES = 3;
const MAX_DESKTOP_LINES = 6;
export const MovieHeader = _ref => {
  let {
    movie
  } = _ref;
  const imagePadding = useToken('space', 'spacing-md');
  const {
    supportingForegroundColor,
    supportingBackgroundColor
  } = useSupportingColors();
  const {
    0: displayReadMore,
    1: setDisplayReadMore
  } = useState(false);
  const {
    0: readmore,
    1: setReadMore
  } = useState(true);
  const checkReadMore = useCallback(element => {
    if (!element) {
      return;
    }
    setDisplayReadMore(element.offsetHeight < element.scrollHeight);
  }, []);
  const {
    0: modalOpen,
    1: setModalOpen
  } = useState(false);
  const {
    title,
    synopsis: description,
    image: posterImage
  } = movie;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Stack, {
      gap: ['spacing-5', 'spacing-5', 'spacing-6'],
      direction: ['column', 'column', 'row'],
      shouldWrapChildren: false,
      flexGrow: 0,
      sx: {
        alignItems: ['left', 'left', 'top'],
        justifyContent: ['inherit', 'inherit', 'center'],
        backgroundColor: supportingBackgroundColor,
        padding: 'spacing-md',
        paddingRight: ['spacing-md', 'spacing-md', 'spacing-xl']
      },
      "data-id": "movie-header_index_MovieHeader_Stack",
      children: [posterImage && /*#__PURE__*/_jsx(Box, {
        sx: {
          flexBasis: '25%',
          minWidth: "calc((100% /  4 ) - (2 * ".concat(imagePadding, "))")
        },
        "data-id": "movie-header_index_MovieHeader_Box",
        children: /*#__PURE__*/_jsx(CloudinaryImage, {
          ratio: "2/3",
          imageSetOrImage: {
            url: posterImage,
            type: 'WIDE'
          },
          sizes: IMAGE_SIZES_HERO_PORTRAIT,
          "data-id": "movie-header_index_MovieHeader_Box_CloudinaryImage"
        })
      }), /*#__PURE__*/_jsxs(Stack, {
        shouldWrapChildren: false,
        sx: {
          marginLeft: ['none', 'none', 'spacing-xl'],
          marginTop: ['none', 'none', 'spacing-3xl'],
          marginBottom: ['none', 'none', 'spacing-xl'],
          justifyContent: ['inherit', 'inherit', 'center'],
          color: supportingForegroundColor,
          flexBasis: [null, null, '75%']
        },
        gap: "spacing-md",
        "data-id": "movie-header_index_MovieHeader_Stack_Stack",
        children: [/*#__PURE__*/_jsxs(Stack, {
          shouldWrapChildren: false,
          direction: "column",
          gap: "spacing-sm",
          align: "flex-start",
          "data-id": "movie-header_index_MovieHeader_Stack_Stack_4",
          children: [/*#__PURE__*/_jsx(Text, {
            isBodyText: true,
            fontSize: ['font-h4', 'font-h4', 'font-h2'],
            tone: "strong",
            "data-id": "movie-header_index_MovieHeader_Stack_Text",
            children: title
          }), /*#__PURE__*/_jsxs(Stack, {
            direction: "row",
            gap: "spacing-lg",
            "data-id": "movie-header_index_MovieHeader_Stack_Stack",
            children: [movie.genre && /*#__PURE__*/_jsx(Text, {
              "data-id": "movie-header_index_MovieHeader_Text",
              children: movie.genre
            }), movie.classification && /*#__PURE__*/_jsx(Text, {
              "aria-label": "classification",
              "data-id": "movie-header_index_MovieHeader_Text_8",
              children: movie.classification
            }), movie.duration && /*#__PURE__*/_jsxs(Text, {
              "aria-label": "duration",
              "data-id": "movie-header_index_MovieHeader_Text_9",
              children: [movie.duration, /*#__PURE__*/_jsx("span", {
                "aria-label": "minutes",
                "data-id": "movie-header_index_MovieHeader_Text_span",
                children: " mins"
              })]
            })]
          })]
        }), /*#__PURE__*/_jsxs(Box, {
          "data-id": "movie-header_index_MovieHeader_Stack_Box",
          children: [/*#__PURE__*/_jsx(Box, {
            noOfLines: [readmore ? MAX_MOBILE_LINES : 0, readmore ? MAX_MOBILE_LINES : 0, readmore ? MAX_DESKTOP_LINES : 0, readmore ? MAX_DESKTOP_LINES : 0],
            sx: {
              lineHeight: 'base',
              maxWidth: '700px'
            },
            ref: checkReadMore,
            "data-id": "movie-header_index_MovieHeader_Box_Box_12",
            children: /*#__PURE__*/_jsx(Markdown, {
              content: description,
              "data-id": "movie-header_index_MovieHeader_Box_Markdown"
            })
          }), displayReadMore && /*#__PURE__*/_jsx(Button, {
            sx: {
              textDecoration: 'underline',
              fontWeight: 'normal',
              padding: 'spacing-none',
              height: 'spacing-md',
              textTransform: 'none',
              color: supportingForegroundColor
            },
            variant: "link",
            onClick: () => {
              setReadMore(!readmore);
            },
            "data-id": "movie-header_index_MovieHeader_Button",
            children: readmore ? 'See all' : 'See less'
          })]
        }), movie.trailerUrl && /*#__PURE__*/_jsx(Box, {
          "data-id": "movie-header_index_MovieHeader_Box",
          children: /*#__PURE__*/_jsx(Button, {
            colorScheme: "licorice",
            variant: "outline",
            ariaLabel: "open-trailer-modal",
            onClick: () => {
              setModalOpen(true);
            },
            "data-id": "movie-header_index_MovieHeader_Box_Button",
            children: "Watch Trailer"
          })
        })]
      })]
    }), movie.trailerUrl && /*#__PURE__*/_jsx(Modal, {
      isOpen: modalOpen,
      onDismissButtonClick: () => {
        setModalOpen(false);
      },
      "data-id": "movie-header_index_MovieHeader_Modal",
      children: modalOpen && /*#__PURE__*/_jsx("video", {
        controls: true,
        autoPlay: true,
        style: {
          width: '100%'
        },
        src: movie.trailerUrl,
        "data-id": "movie-header_index_MovieHeader_video",
        children: /*#__PURE__*/_jsx("track", {
          kind: "captions",
          src: movie === null || movie === void 0 ? void 0 : movie.trailerCaptions,
          "data-id": "movie-header_index_MovieHeader_video_track"
        })
      })
    })]
  });
};